/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { listActivePriceListsLocal, type PriceList } from '../../../priceLists/public-api';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ModalNestedPicker } from '../../../shared/modals/ModalNestedPicker';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities'
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider'
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService';
import { usePosPriceListStore } from '../../store/usePosPriceListStore';
import { resolvePriceListPresentation } from '../../application/posCheckoutPresentation';

const NONE_VALUE = '__none__';

/** Capability + flag + fetch de listas. El panel lo usa para decidir visibilidad
 * (p.ej. el grupo «Más opciones» en mobile) sin duplicar el fetch. */
export function useCheckoutPriceLists(): { enabled: boolean; lists: PriceList[] } {
  const hasPriceLists = useHasBusinessCapability('price_lists');
  const { flags } = useGlobalFeatureFlags();
  const enabled = hasPriceLists && isGlobalFeatureOn(flags, 'price_lists_integrations');
  const [lists, setLists] = useState<PriceList[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void listActivePriceListsLocal()
      .then((rows) => {
        if (!cancelled) setLists(rows);
      })
      .catch(() => {
        if (!cancelled) setLists([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { enabled, lists };
}

export function PosCheckoutPriceListSelector({
  enabled,
  lists,
}: {
  enabled: boolean;
  lists: PriceList[];
}) {
  const activePriceListId = usePosPriceListStore((s) => s.activePriceListId);
  const setActivePriceListId = usePosPriceListStore((s) => s.setActivePriceListId);
  const [pickerVisible, setPickerVisible] = useState(false);

  const presentation = resolvePriceListPresentation(lists.length, !!activePriceListId);
  if (!enabled || presentation === 'hidden') return null;

  const options: Array<{ id: string | null; label: string }> = [
    { id: null, label: 'Sin lista' },
    ...lists.map((list) => ({ id: list.id, label: list.name })),
  ];
  const selectedLabel =
    options.find((opt) => (opt.id ?? null) === (activePriceListId ?? null))?.label ?? 'Sin lista';

  // Muchas listas → picker con buscador (no un muro de chips).
  if (presentation === 'picker') {
    return (
      <View style={selectorStyles.row} testID="pos.checkout.price-list-selector">
        <Text style={selectorStyles.label}>Lista de precio</Text>
        <TouchableOpacity
          style={modalExperience.entitySelector}
          onPress={() => setPickerVisible(true)}
          testID="pos.checkout.price-list.open-picker"
          accessibilityRole="button"
          accessibilityLabel={`Lista de precio: ${selectedLabel}`}
        >
          <Text style={modalExperience.filterSelectValue} numberOfLines={1}>
            {selectedLabel}
          </Text>
          <Ionicons name="chevron-down" size={16} color={ticketColors.ink3} />
        </TouchableOpacity>
        <ModalNestedPicker
          visible={pickerVisible}
          title="Lista de precio"
          options={options.map((opt) => ({ value: opt.id ?? NONE_VALUE, label: opt.label }))}
          selectedValue={activePriceListId ?? NONE_VALUE}
          onSelect={(value) => setActivePriceListId(value === NONE_VALUE ? null : value)}
          onClose={() => setPickerVisible(false)}
        />
      </View>
    );
  }

  return (
    <View style={selectorStyles.row} testID="pos.checkout.price-list-selector">
      <Text style={selectorStyles.label}>Lista de precio</Text>
      <View style={selectorStyles.chips}>
        {options.map((opt) => {
          const selected = (opt.id ?? null) === (activePriceListId ?? null);
          return (
            <TouchableOpacity
              key={opt.id ?? 'none'}
              style={[selectorStyles.chip, selected && selectorStyles.chipActive]}
              onPress={() => setActivePriceListId(opt.id)}
              testID={
                selected
                  ? `pos.checkout.price-list.selected.${opt.id ?? 'none'}`
                  : `pos.checkout.price-list.chip.${opt.id ?? 'none'}`
              }
              accessibilityRole="radio"
              accessibilityLabel={opt.label}
              accessibilityState={{ selected, checked: selected }}
            >
              <Text style={[selectorStyles.chipText, selected && selectorStyles.chipTextActive]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const selectorStyles = StyleSheet.create({
  row: { gap: spacing.xs, marginBottom: spacing.sm },
  label: { fontSize: 13, fontWeight: '600', color: ticketColors.ink2 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  chip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  chipActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold + '22',
  },
  chipText: { fontSize: 12, fontWeight: '600', color: ticketColors.ink2 },
  chipTextActive: { color: ticketColors.gold, fontWeight: '800' },
});
